import { useState } from 'react';
import { Button, Progress } from '@eureca/ui';
import { EurecaLogo } from '@eureca/ui/icons';

const THEME_KEY = 'client-theme';

function MoonIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-4"
      aria-hidden
    >
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </svg>
  );
}
function SunIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-4"
      aria-hidden
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

/** Header global das telas do formulário (Figma "Candidates menu"): logo + tema. */
export function AppBar() {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  return (
    <header className="flex h-16 items-center justify-between border-b border-border px-8">
      <EurecaLogo aria-label="Eureca" role="img" className="h-[19px] w-[112px] text-primary" />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={dark ? 'Usar tema claro' : 'Usar tema escuro'}
        onClick={() => {
          const next = document.documentElement.classList.toggle('dark');
          setDark(next);
          try {
            localStorage.setItem(THEME_KEY, next ? 'dark' : 'light');
          } catch {
            // preferência só nesta aba
          }
        }}
      >
        {dark ? <SunIcon /> : <MoonIcon />}
      </Button>
    </header>
  );
}

/**
 * Avatar do programa (QA 08/10/2026): o logo quando o programa tem um; senão a
 * inicial sobre fundo neutro — o mesmo fallback do `WorkspaceBrand` do consultor.
 * Sem isso o cabeçalho ficava sem marca nenhuma para empresa sem logo.
 */
export function ProgramAvatar({ name, logoUrl }: { name: string; logoUrl: string | null }) {
  const label = `Logo de ${name}`;
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={label}
        className="size-8 shrink-0 rounded-md object-cover"
        data-testid="program-avatar-logo"
      />
    );
  }
  return (
    <div
      role="img"
      aria-label={label}
      className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-sm font-medium text-foreground"
      data-testid="program-avatar-fallback"
    >
      {name.trim().charAt(0).toUpperCase() || '—'}
    </div>
  );
}

/** Cabeçalho do programa + nome da etapa + progresso (coluna de 640px). */
export function ProgramHeader({
  programName,
  logoUrl,
  stepTitle,
  progress,
}: {
  programName: string;
  logoUrl: string | null;
  stepTitle: string;
  progress: number;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <ProgramAvatar name={programName} logoUrl={logoUrl} />
        <h1 className="text-4xl font-medium leading-10 text-foreground">{programName}</h1>
      </div>
      <h2 className="text-xl font-medium text-accent-foreground">{stepTitle}</h2>
      <Progress value={progress} aria-label={`${stepTitle}: ${progress}%`} />
    </div>
  );
}
